import React, { useState, useRef, useEffect, useCallback } from "react";
import {
    Dialog, DialogTitle, DialogContent, DialogActions,
    Button, TextField, Typography, Box, Avatar,
    CircularProgress, Alert,
} from "@mui/material";
import PersonIcon from "@mui/icons-material/Person";
import { Html5Qrcode } from "html5-qrcode";
import { useDispatch } from "react-redux";
import { useNavigate } from "react-router-dom";
import { updateUserInfo } from "../../reducers/userSlice";
import { setLocalStorageUser, getLocalStorageUser } from "../../helper/LocalStorageHelper";
import { MemberApi } from "../../api/MemberApi";
import {
    startHtml5QrcodeCamera,
    stopHtml5QrcodeCamera,
} from "../../helper/html5QrcodeScanner";
import {
    dialogPaperSx,
    dialogTitleSx,
    dialogContentSx,
    innerPanelSx,
    textFieldSx,
    primaryButtonSx,
    outlinedButtonSx,
    subheadingSx,
    mutedTextSx,
    avatarSx,
    loadingSpinnerSx,
    rowDividerSx,
} from "../../styles/cyberTheme";

const STEP_SCAN = "scan";
const STEP_CONFIRM = "confirm";
const QR_READER_ID = "skillpassport-qr-reader";

export default function SkillpassportQRLoginDialog({
    open,
    onClose,
    skipNavigateOnConfirm = false,
}) {
    const dispatch = useDispatch();
    const navigate = useNavigate();

    const [step, setStep] = useState(STEP_SCAN);
    const [qrInput, setQrInput] = useState("");
    const [loading, setLoading] = useState(false);
    const [error, setError] = useState("");
    const [cameraError, setCameraError] = useState("");
    const [memberInfo, setMemberInfo] = useState(null);

    const html5QrCodeRef = useRef(null);
    const scanInFlightRef = useRef(false);
    const restartScannerRef = useRef(null);
    const openRef = useRef(open);
    const stepRef = useRef(step);

    openRef.current = open;
    stepRef.current = step;

    // Reset state whenever dialog opens
    useEffect(() => {
        if (open) {
            setStep(STEP_SCAN);
            setQrInput("");
            setMemberInfo(null);
            setError("");
            setCameraError("");
            scanInFlightRef.current = false;
        }
    }, [open]);

    const handleScan = useCallback(async (valueOverride) => {
        const value = (valueOverride ?? qrInput).trim();
        if (!value || scanInFlightRef.current) return;

        scanInFlightRef.current = true;
        await stopHtml5QrcodeCamera(html5QrCodeRef.current);
        html5QrCodeRef.current = null;

        setLoading(true);
        setError("");
        let loginSuccess = false;

        try {
            const result = await MemberApi.loginByQrCode(value);
            const jwt = result?.jwt;
            const userInfo = result?.user;
            const orgName = result?.organization?.name_zh || "";

            if (!jwt || !userInfo) {
                setError("登入失敗，請重試。");
                return;
            }

            userInfo.jwt = jwt;
            console.log("userInfo: ", userInfo);
            loginSuccess = true;
            setMemberInfo({ ...userInfo, jwt, org_name: orgName });
            setStep(STEP_CONFIRM);
        } catch (e) {
            console.error("QR login error:", e);
            setError("登入失敗，請重試。");
        } finally {
            setLoading(false);
            if (!loginSuccess && openRef.current && stepRef.current === STEP_SCAN) {
                scanInFlightRef.current = false;
                restartScannerRef.current?.();
            }
        }
    }, [qrInput]);

    const handleScanRef = useRef(handleScan);
    handleScanRef.current = handleScan;

    useEffect(() => {
        if (!open || step !== STEP_SCAN) {
            stopHtml5QrcodeCamera(html5QrCodeRef.current);
            html5QrCodeRef.current = null;
            return undefined;
        }

        let cancelled = false;

        const startScanner = async () => {
            if (cancelled || scanInFlightRef.current) return;
            if (!document.getElementById(QR_READER_ID)) return;

            setCameraError("");
            await stopHtml5QrcodeCamera(html5QrCodeRef.current);
            html5QrCodeRef.current = new Html5Qrcode(QR_READER_ID, false);

            try {
                await startHtml5QrcodeCamera(html5QrCodeRef.current, (decodedText) => {
                    if (cancelled || scanInFlightRef.current) return;
                    setQrInput(decodedText);
                    handleScanRef.current(decodedText);
                });

                if (cancelled && html5QrCodeRef.current?.isScanning) {
                    await stopHtml5QrcodeCamera(html5QrCodeRef.current);
                    html5QrCodeRef.current = null;
                }
            } catch (err) {
                if (!cancelled) {
                    const message = err?.message || String(err);
                    setCameraError(message);
                    console.error("Failed to start QR camera", err);
                }
            }
        };

        restartScannerRef.current = startScanner;

        const timer = setTimeout(() => {
            startScanner();
        }, 0);

        return () => {
            cancelled = true;
            clearTimeout(timer);
            stopHtml5QrcodeCamera(html5QrCodeRef.current);
            html5QrCodeRef.current = null;
        };
    }, [open, step]);

    const handleConfirm = () => {
        if (!memberInfo) return;
        dispatch(updateUserInfo(memberInfo));
        setLocalStorageUser(memberInfo);

        console.log("memberInfo: ", memberInfo);

        // Testing getLocalStorageUser
        const stored = getLocalStorageUser();
        console.log("stored: ", JSON.parse(stored));

        onClose();
        if (!skipNavigateOnConfirm) {
            navigate("/member-panel");
        }
    };

    const handleCancel = () => {
        setMemberInfo(null);
        setStep(STEP_SCAN);
        setQrInput("");
        setError("");
        setCameraError("");
        scanInFlightRef.current = false;
        onClose();
    };

    return (
        <Dialog
            open={open}
            onClose={handleCancel}
            maxWidth="xs"
            fullWidth
            PaperProps={{ sx: dialogPaperSx }}
        >
            <DialogTitle
                sx={{
                    ...dialogTitleSx,
                    textAlign: "center",
                    fontSize: "1.4rem",
                    pb: 1,
                    pr: 3,
                }}
            >
                帳號登入
            </DialogTitle>

            <DialogContent sx={{ ...dialogContentSx, px: 3, pb: 1 }}>
                {step === STEP_SCAN && (
                    <Box
                        sx={{
                            ...innerPanelSx,
                            p: 3,
                            display: "flex",
                            flexDirection: "column",
                            alignItems: "center",
                            gap: 2,
                            justifyContent: "center",
                        }}
                    >
                        <Typography variant="h6" sx={{ ...subheadingSx, lineHeight: 1.2 }}>
                            請掃描QR Code
                        </Typography>
                        <Typography variant="body2" sx={mutedTextSx}>
                            請將 QR Code 對準鏡頭
                        </Typography>
                        <Box
                            sx={{
                                position: "relative",
                                width: "100%",
                            }}
                        >
                            <Box
                                id={QR_READER_ID}
                                sx={{
                                    width: "100%",
                                    minHeight: 280,
                                    overflow: "hidden",
                                    opacity: loading ? 0.5 : 1,
                                    "& video": {
                                        width: "100% !important",
                                        maxHeight: 360,
                                    },
                                }}
                            />
                            {loading && (
                                <CircularProgress
                                    size={40}
                                    sx={{
                                        ...loadingSpinnerSx,
                                        position: "absolute",
                                        top: "50%",
                                        left: "50%",
                                        mt: "-20px",
                                        ml: "-20px",
                                    }}
                                />
                            )}
                        </Box>
                        {cameraError && (
                            <Alert severity="error" sx={{ width: "100%" }}>
                                無法啟動鏡頭：{cameraError}。請允許相機權限後重試。
                            </Alert>
                        )}
                        <Typography variant="body2" sx={mutedTextSx}>
                            或以文字輸入 QR Code
                        </Typography>
                        <TextField
                            fullWidth
                            value={qrInput}
                            onChange={(e) => {
                                setQrInput(e.target.value);
                                setError("");
                            }}
                            onKeyDown={(e) => {
                                if (e.key === "Enter") handleScan();
                            }}
                            autoComplete="off"
                            variant="outlined"
                            size="medium"
                            disabled={loading}
                            error={!!error}
                            helperText={error}
                            sx={textFieldSx}
                        />
                    </Box>
                )}

                {step === STEP_CONFIRM && memberInfo && (
                    <Box sx={{ ...innerPanelSx, p: 2.5 }}>
                        <Box sx={{ display: "flex", alignItems: "center", gap: 2, mb: 1.5 }}>
                            <Avatar sx={{ ...avatarSx, width: 48, height: 48 }}>
                                <PersonIcon />
                            </Avatar>
                            <Box>
                                <Typography variant="h6" sx={{ ...subheadingSx, lineHeight: 1.2 }}>
                                    {memberInfo.display_name || memberInfo.name}
                                </Typography>
                                <Typography variant="body2" sx={mutedTextSx}>
                                    {memberInfo.email}
                                </Typography>
                            </Box>
                        </Box>
                        <Box sx={{ ...rowDividerSx, mb: 1.5 }} />
                        {memberInfo.user_type && (
                            <Typography variant="body2" sx={mutedTextSx}>
                                會員類型：{memberInfo.user_type}
                            </Typography>
                        )}
                    </Box>
                )}
            </DialogContent>

            <DialogActions sx={{ justifyContent: "center", px: 3, pb: 3, pt: 2 }}>
                {step === STEP_SCAN && (
                    <Button
                        onClick={() => handleScan()}
                        disabled={loading || !qrInput.trim()}
                        variant="contained"
                        fullWidth
                        sx={{
                            ...primaryButtonSx,
                            fontSize: "1rem",
                            py: 1.2,
                        }}
                    >
                        {loading ? (
                            <CircularProgress size={22} sx={loadingSpinnerSx} />
                        ) : (
                            "確定登入"
                        )}
                    </Button>
                )}

                {step === STEP_CONFIRM && (
                    <>
                        <Button
                            onClick={handleCancel}
                            variant="outlined"
                            sx={{ ...outlinedButtonSx, flex: 1 }}
                        >
                            取消
                        </Button>
                        <Button
                            onClick={handleConfirm}
                            variant="contained"
                            sx={{ ...primaryButtonSx, flex: 1 }}
                        >
                            確認登入
                        </Button>
                    </>
                )}
            </DialogActions>
        </Dialog>
    );
}
